import { useRef, useState } from "react";
import { ApiError, type ImportPlan, type Location, api, formatMoney } from "./api";
import { singaporeToday } from "./dates";
import { SelectField } from "./fields";
import { Alert, Badge, Empty, Modal, Table, Th, TwoLine, Working } from "./ui";

/**
 * A stock list from a spreadsheet (quotation A24).
 *
 * Nobody types three hundred rows into a screen the week before a show, so the file comes
 * in as it is: .xlsx from Excel or Google Sheets, or a CSV. Every row is read and shown
 * back first, with anything the system could not read named by its row number, and
 * nothing is written until the person has seen that list and says to go ahead.
 */
export function StockImport({
  locations,
  onClose,
  onDone,
}: {
  readonly locations: readonly Location[];
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const [file, setFile] = useState<{ name: string; content: string } | undefined>();
  const [plan, setPlan] = useState<ImportPlan | undefined>();
  const [onDate, setOnDate] = useState(singaporeToday());
  const [locationId, setLocationId] = useState("");
  const [acquisition, setAcquisition] = useState<"opening_balance" | "purchase" | "gift">("opening_balance");
  /**
   * A price list, not a count. Before the shelves have been counted, a list of what the
   * shop sells and what it charges is still worth having: the booth sells from it and
   * every sale is recorded, and no figure pretends to know how many there were.
   */
  const [uncounted, setUncounted] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState<"reading" | "importing" | undefined>();
  const picker = useRef<HTMLInputElement>(null);

  async function choose(chosen: File | undefined): Promise<void> {
    if (!chosen) return;
    setBusy("reading");
    setProblem(undefined);
    setPlan(undefined);
    try {
      const content = await base64(chosen);
      setFile({ name: chosen.name, content });
      const result = await api.previewStockImport(chosen.name, content, uncounted);
      setPlan(result.plan);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The file could not be read");
      setFile(undefined);
    } finally {
      setBusy(undefined);
      if (picker.current) picker.current.value = "";
    }
  }

  async function go(): Promise<void> {
    if (!file || !plan) return;
    setBusy("importing");
    setProblem(undefined);
    try {
      const { result } = await api.importStock({
        fileName: file.name,
        content: file.content,
        onDate,
        locationId: locationId || null,
        acquisition,
        skipProblems: plan.totals.problems > 0,
        uncounted,
      });
      const made = result.productsCreated;
      const changed = result.productsUpdated;
      // A price list brings no stock in at all, so the sentence leads with the products.
      const parts = [
        made > 0 ? `${made} new ${made === 1 ? "product" : "products"}` : null,
        changed > 0 ? `${changed} ${changed === 1 ? "product" : "products"} updated` : null,
        result.lots > 0 ? `${result.lots} inventory ${result.lots === 1 ? "row" : "rows"} added, ${formatMoney(result.costCents)} at cost` : null,
      ].filter(Boolean);
      onDone(
        (parts.length > 0 ? parts.join(", ") : "No changes") +
          (result.skipped > 0 ? `. ${result.skipped} ${result.skipped === 1 ? "row" : "rows"} excluded.` : "."),
      );
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not save");
    } finally {
      setBusy(undefined);
    }
  }

  const ready = plan ? plan.totals.ready : 0;

  return (
    <Modal
      open
      size="lg"
      title="Import product or inventory list"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy !== undefined || ready === 0}
            onClick={() => void go()}
          >
            {busy === "importing" ? (
              <Working label="Importing" />
            ) : (
              `Import ${ready} ${ready === 1 ? "row" : "rows"}`
            )}
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <label htmlFor="stock-file" className="form-label">
            File
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="ti-btn ti-btn-light !mb-0"
              disabled={busy !== undefined}
              onClick={() => picker.current?.click()}
            >
              {busy === "reading" ? <Working label="Reading" /> : <><i className="ri-file-excel-2-line" /> Select file</>}
            </button>
            <span className="text-[#8c9097] dark:text-white/50">
              {file ? file.name : "Excel (.xlsx) or CSV with a header row"}
            </span>
          </div>
          <input
            ref={picker}
            id="stock-file"
            type="file"
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(changed) => void choose(changed.target.files?.[0])}
          />
        </div>

        <div className="sm:col-span-4 col-span-12">
          <label htmlFor="stock-date" className="form-label">
            Inventory date
          </label>
          <input
            id="stock-date"
            type="date"
            className="form-control"
            value={onDate}
            max={singaporeToday()}
            onChange={(changed) => setOnDate(changed.target.value)}
          />
        </div>
        <div className="sm:col-span-4 col-span-12">
          <label htmlFor="stock-place" className="form-label">
            Inventory location
          </label>
          <SelectField
            id="stock-place"
            value={locationId}
            placeholder="Select"
            options={locations.map((location) => ({ value: location.id, label: location.name }))}
            onChange={setLocationId}
          />
        </div>
        <div className="col-span-12">
          <label className="flex items-start gap-2" htmlFor="stock-uncounted">
            <input
              id="stock-uncounted"
              type="checkbox"
              className="form-check-input mt-1"
              checked={uncounted}
              onChange={(event) => {
                setUncounted(event.target.checked);
                setPlan(undefined);
                setFile(undefined);
              }}
            />
            <span>
              <span className="font-semibold">Import prices only</span>
              <span className="block text-[0.75rem] text-[#8c9097] dark:text-white/50">
                Import product details, prices and costs without entering an inventory count. Quantities are not
                required.
              </span>
            </span>
          </label>
        </div>
        <div className="sm:col-span-4 col-span-12">
          <label htmlFor="stock-kind" className="form-label">
            Inventory category
          </label>
          <SelectField
            id="stock-kind"
            value={acquisition}
            options={[
              { value: "opening_balance", label: "Opening inventory" },
              { value: "purchase", label: "New purchase" },
              { value: "gift", label: "Received free of charge" },
            ]}
            onChange={(value) => setAcquisition(value as typeof acquisition)}
          />
        </div>
      </div>

      {plan ? (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Badge tone="success">{plan.totals.ready} {plan.totals.ready === 1 ? "row" : "rows"} ready to import</Badge>
            {plan.totals.problems > 0 ? <Badge tone="danger">{plan.totals.problems} {plan.totals.problems === 1 ? "row" : "rows"} cannot be read</Badge> : null}
            {/* Said as a number, because a sheet meant to change products already on the
                system should say 0 new, and one row misspelt is easy to miss in a hundred. */}
            <Badge tone={plan.rows.some((row) => row.newProduct && !row.problem) ? "info" : "light"}>
              {plan.rows.filter((row) => row.newProduct && !row.problem).length} new products
            </Badge>
            <span className="text-[#8c9097] dark:text-white/50">
              {plan.sheetName ? `Sheet "${plan.sheetName}", ` : ""}
              {formatMoney(plan.totals.costCents)} at cost
            </span>
            <div className="flex-1" />
            <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
              Columns detected: {Object.values(plan.columns).join(", ")}
            </span>
          </div>

          {plan.rows.length === 0 ? (
            <Empty icon="ri-file-list-3-line">No rows were found below the header row.</Empty>
          ) : (
            <div className="max-h-[22rem] overflow-y-auto">
              <Table
                head={
                  <>
                    <Th>Row</Th>
                    <Th>Item</Th>
                    <Th end>Quantity</Th>
                    <Th end>Cost</Th>
                    <Th end>Price</Th>
                    <Th>Location</Th>
                  </>
                }
              >
                {plan.rows.map((row) => (
                  <tr key={row.line} className="border-b border-defaultborder">
                    <td className="text-[#8c9097] dark:text-white/50">{row.line}</td>
                    <td>
                      <TwoLine
                        main={
                          <span className="flex items-center gap-2">
                            {row.name || <span className="text-danger">No product name</span>}
                            {row.kind === "slab" ? <Badge tone="secondary">Graded card</Badge> : null}
                            {row.newProduct ? <Badge tone="info">New</Badge> : null}
                            {row.notForSale === true ? <Badge tone="warning">Not available for sale</Badge> : null}
                            {row.notForSale === false ? <Badge tone="light">Available for sale</Badge> : null}
                          </span>
                        }
                        sub={[row.code, row.section].filter(Boolean).join(" · ") || undefined}
                      />
                      {row.problem ? <span className="text-danger text-[0.75rem]">{row.problem}</span> : null}
                    </td>
                    <td className="!text-end">{row.quantity}</td>
                    <td className="!text-end">{formatMoney(row.totalCostCents)}</td>
                    <td className="!text-end">{row.priceCents === null ? "-" : formatMoney(row.priceCents)}</td>
                    <td className="text-[#8c9097] dark:text-white/50">{row.locationName ?? "-"}</td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
          {plan.totals.problems > 0 ? (
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-0">
              Rows marked in red are excluded. Correct them in the spreadsheet and import the file again.
            </p>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

/** The file as base64, which is how it travels: a spreadsheet is not text. */
async function base64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let at = 0; at < buffer.length; at += 0x8000) {
    binary += String.fromCharCode(...buffer.subarray(at, at + 0x8000));
  }
  return btoa(binary);
}
